import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { getAuthHeaders } from 'services/api';
import { error, log } from 'utils/logger';
import { joinUrl } from 'lib/utils';

interface GitHubContextType {
  githubConnected: boolean;
  isConnecting: boolean;
  isDisconnecting: boolean;
  isLoading: boolean;
  checkGithubAuth: () => Promise<void>;
  setGithubConnected: (connected: boolean) => void;
  setIsConnecting: (connecting: boolean) => void;
  setIsDisconnecting: (disconnecting: boolean) => void;
}

const GitHubContext = createContext<GitHubContextType | undefined>(undefined);

export const GitHubProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [githubConnected, setGithubConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const checkGithubAuth = useCallback(async () => {
    setIsLoading(true);
    try {
      const headers = getAuthHeaders()
      if (!headers) {
        error('Auth token not found');
        setGithubConnected(false);
        setIsLoading(false);
        return;
      }

      log('Checking GitHub auth status...');
      const response = await axios.get(joinUrl(process.env.REACT_APP_MIDDLEWARE_BACKEND_URL || '', 'github/check-auth'), {
        headers,
        withCredentials: true
      });

      const data = response.data;
      log('GitHub auth check response:', data);
      
      setGithubConnected(data.message === "Authenticated");
    } catch (err : any) {
      error('Error checking GitHub auth:', err);
      setGithubConnected(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    // Only check auth if we have auth headers
    const headers = getAuthHeaders();
    if (headers) {
      checkGithubAuth();
    } else {
      setIsLoading(false);
      setGithubConnected(false);
    }
  }, [checkGithubAuth]);

return (
      <GitHubContext.Provider value={{
        githubConnected,
        isConnecting,
        isDisconnecting,
        isLoading,
        checkGithubAuth,
        setGithubConnected,
        setIsConnecting,
        setIsDisconnecting
      }}>
      {children}
    </GitHubContext.Provider>
  );
};

export const useGitHub = () => {
  const context = useContext(GitHubContext);
  if (context === undefined) {
    throw new Error('useGitHub must be used within a GitHubProvider');
  }
  return context;
};
